import { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { StructureSetId } from '~/domain/changes/ids';
import { DicomColor } from '~/domain/dicom/dicomColor';
import {
    RoiGenerationAlgorithmType,
    RoiInterpretedType,
} from '~/domain/dicom/dicomstructureset';
import { ContourTexture } from '~/engine/data/structure/ContourTexture';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import { GlOperationGuard } from '~/engine/util/algorithms/sdf/utils';
import {
    computeUnionTexture,
    UNION_OPERATION,
} from '~/engine/util/algorithms/sdf/unionPipeline';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { useGetOrCreateDestination } from './useGetOrCreateDestination';
import { useStructureSetCommands } from '~/hooks/domain/useStructureSetCommands';
import { getErrorMessage } from '~/utils/errors';
import { enqueueSnackbar } from '~/utils/notifications';
import { waitForUI } from '~/utils/uiUtils';

/** One bulk-union apply, fully resolved from the dialog's own gating. */
export interface BulkUnionRequest {
    structureSetId: StructureSetId;
    /**
     * Sources to union, in panel order. The DIALOG has already dropped
     * point ROIs and ROIs without engine geometry and surfaced what it
     * dropped, so this list is exactly what the union consumes.
     */
    sourceRoiNumbers: readonly number[];
    /**
     * Sources to delete afterwards — empty when the user did not opt in,
     * and never containing an approved structure (the dialog excludes
     * them, because {@link useStructureSetCommands.removeRois} is
     * all-or-nothing and would reject the whole batch).
     */
    deleteRoiNumbers: readonly number[];
    name: string;
    interpretedType: RoiInterpretedType;
    color: DicomColor;
    /** Per-slice (2D) hole fill, run after the union fold and before the
     *  margin. Opt-in — a union of authored structures may legitimately
     *  contain internal gaps the user wants kept. */
    fillHoles2d: boolean;
    /** Domain unit; the dialog converts from the cm it displays. */
    marginMm: number;
    /** cm³ — the legacy unit of the remove-small-parts stage. */
    minVolumeCm3?: number;
    /** EXTERNAL to intersect with, or null when none is usable. */
    keepInsideRoiNumber: number | null;
    /**
     * Skin margin in mm — how far INSIDE the EXTERNAL surface the clip
     * boundary lies; 0 clips at the surface. The dialog converts from the
     * cm it displays. Meaningful only with a non-null
     * {@link keepInsideRoiNumber}; the contraction is computed on a
     * scratch copy, never on the EXTERNAL's own texture.
     */
    keepInsideContractionMm: number;
}

export interface BulkUnionOutcome {
    createdRoiNumber: number;
    /** Empty when delete-sources was off OR when the deletion was rejected. */
    deletedRoiNumbers: readonly number[];
    /**
     * Distinguishes "delete refused" from "delete not requested" — both
     * leave {@link deletedRoiNumbers} empty. Callers that drop UI state
     * (selection, active ROI) MUST keep it on 'refused' (the
     * {@link useStructureSetCommands.removeRois} contract): the sources
     * are all still present, and a cleared selection would force the user
     * to re-pick every structure to retry the delete.
     */
    deleteOutcome: 'not-requested' | 'deleted' | 'refused';
}

/**
 * Applies the bulk-union operation (SR-WOR-H-26-2): union the selected
 * structures, run the post-union stages, write the result into a NEW
 * structure, and optionally delete the sources.
 *
 * Ordering of side effects is deliberate — texture first, destination
 * second, deletion last. Nothing is created until a committable texture
 * exists (a GPU failure cannot leave an empty structure behind), and
 * nothing is deleted until the union has actually landed.
 */
export function useBulkUnion() {
    const intl = useIntl();
    const { environment } = useActiveEditTarget();
    const [isProcessing, setIsProcessing] = useState(false);
    // Synchronous mirror of the flag above. `isProcessing` is captured in
    // this callback's closure and only refreshes on re-render, and the GL
    // guard is not acquired until after the `waitForUI` yield below — so
    // two Apply clicks landing before the disabled state repaints would
    // both pass and create "Union" and "Union (1)".
    const isProcessingRef = useRef(false);
    const { getOrCreateDestination } = useGetOrCreateDestination({
        environment: environment ?? null,
    });
    const { removeRois } = useStructureSetCommands();

    const runUnion = useCallback(
        async (
            request: BulkUnionRequest,
            /**
             * Hands off a texture the panel's preview has already computed
             * for EXACTLY this request's geometry parameters — the dialog
             * matches by preview key ({@link bulkUnionPreviewKey}) before
             * offering one, so a stale preview returns undefined and the
             * union is computed from the sources as before. Invoked only
             * AFTER the busy/guard refusals, so a refused run never
             * consumes (and thereby destroys) a held on-screen preview.
             */
            getPrecomputedTexture?: () => ContourTexture | undefined
        ): Promise<BulkUnionOutcome | null> => {
            if (isProcessingRef.current || GlOperationGuard.isActive) {
                // A synchronous check, unlike the Recoil-backed
                // isProcessing: it is what actually closes the
                // double-click race (see useRoiSdfOperation).
                enqueueSnackbar(
                    intl.formatMessage({ id: 'structure.bulk.union.busy' }),
                    { variant: 'warning' }
                );
                return null;
            }
            const structureSet = environment?.structureSet;
            if (!structureSet) {
                return null;
            }

            isProcessingRef.current = true;
            setIsProcessing(true);
            // Ownership marker for the release-with-acquisition rule: held
            // from the moment computeUnionTexture returns until
            // replaceTexture hands the texture to the ROI.
            let ownedTexture: ContourTexture | undefined;
            let dest: RegionOfInterest | null = null;
            try {
                // Texture-acquisition fork: a matching held preview is
                // committed as-is (it ran the identical pipeline), else the
                // union is computed from the sources. Both paths end with
                // this hook owning exactly one unattached texture.
                let texture = getPrecomputedTexture?.();
                if (!texture) {
                    const sources: ContourTexture[] = [];
                    for (const roiNumber of request.sourceRoiNumbers) {
                        const sourceTexture =
                            structureSet.getRoi(roiNumber)?.texture;
                        if (!sourceTexture || sourceTexture.isEmpty) {
                            // A source that lost its geometry between the
                            // dialog rendering and Apply. Silently dropping
                            // it would deliver a result SMALLER than the
                            // user configured, so refuse instead.
                            throw new Error(
                                intl.formatMessage({
                                    id: 'structure.bulk.union.sourceGone',
                                })
                            );
                        }
                        sources.push(sourceTexture);
                    }

                    let keepInsideTexture: ContourTexture | undefined;
                    if (request.keepInsideRoiNumber !== null) {
                        keepInsideTexture = structureSet.getRoi(
                            request.keepInsideRoiNumber
                        )?.texture;
                        if (!keepInsideTexture) {
                            // Same rule the threshold segmentation applies
                            // (resolveKeepInsideTexture): a requested clip
                            // stage that cannot run must fail, never skip —
                            // skipping delivers a result LARGER than
                            // configured.
                            throw new Error(
                                intl.formatMessage({
                                    id: 'structure.bulk.union.externalGone',
                                })
                            );
                        }
                    }

                    await waitForUI();
                    texture = await computeUnionTexture(sources, {
                        fillHoles2d: request.fillHoles2d,
                        marginMm: request.marginMm,
                        minVolumeCm3: request.minVolumeCm3,
                        keepInsideTexture,
                        keepInsideContractionMm:
                            request.keepInsideContractionMm,
                    });
                }
                ownedTexture = texture;

                if (texture.isEmpty) {
                    // A contraction margin or a disjoint EXTERNAL can empty
                    // a non-empty union. Refuse BEFORE any structure exists,
                    // so nothing is created and no success is announced.
                    texture.delete();
                    ownedTexture = undefined;
                    enqueueSnackbar(
                        intl.formatMessage({
                            id: 'structure.bulk.union.emptyResult',
                        }),
                        { variant: 'error' }
                    );
                    return null;
                }

                dest = await getOrCreateDestination(null, request.name, {
                    interpretedType: request.interpretedType,
                    color: request.color,
                    generation: {
                        // SEMI_AUTOMATIC, not AUTOMATIC: the geometry comes
                        // from human-authored structures combined by an
                        // automatic operation. (Threshold segmentation
                        // stamps AUTOMATIC because its input is the image.)
                        algorithm: RoiGenerationAlgorithmType.SEMI_AUTOMATIC,
                        description: buildGenerationDescription(request),
                    },
                });
                if (!dest) {
                    texture.delete();
                    ownedTexture = undefined;
                    return null;
                }

                dest.replaceTexture(texture, UNION_OPERATION);
                environment.triggerContourLayerUpdate();

                let deletedRoiNumbers: readonly number[] = [];
                let deleteOutcome: BulkUnionOutcome['deleteOutcome'] =
                    'not-requested';
                if (request.deleteRoiNumbers.length > 0) {
                    // removeRois is all-or-nothing and disposes each deleted
                    // ROI's undo snapshot and baseline itself
                    // (StructureSetService.forgetRoiSnapshots) — the
                    // recycled-ROINumber hazard is handled at that single
                    // choke point, never here. A rejection surfaces as a
                    // snackbar and returns false; the union structure stays
                    // (it succeeded) and the refusal is REPORTED in the
                    // outcome so the caller can keep the selection instead
                    // of clearing it over structures that still exist.
                    const sorted = [...request.deleteRoiNumbers].sort(
                        (a, b) => a - b
                    );
                    if (removeRois(request.structureSetId, sorted)) {
                        deletedRoiNumbers = sorted;
                        deleteOutcome = 'deleted';
                    } else {
                        deleteOutcome = 'refused';
                    }
                }

                enqueueSnackbar(
                    intl.formatMessage(
                        { id: 'structure.bulk.union.success' },
                        { name: dest.name }
                    ),
                    { variant: 'success' }
                );
                return {
                    createdRoiNumber: dest.roiNumber,
                    deletedRoiNumbers,
                    deleteOutcome,
                };
            } catch (e) {
                // Release the texture unless replaceTexture already handed
                // it to the ROI (it assigns before the undo snapshot, which
                // can itself throw — deleting then would free a texture the
                // ROI now owns).
                if (ownedTexture && dest?.texture !== ownedTexture) {
                    ownedTexture.delete();
                }
                console.error('Bulk union failed:', e);
                enqueueSnackbar(
                    intl.formatMessage(
                        { id: 'structure.bulk.union.failed' },
                        { error: getErrorMessage(e) }
                    ),
                    { variant: 'error' }
                );
                return null;
            } finally {
                isProcessingRef.current = false;
                setIsProcessing(false);
            }
        },
        [environment, getOrCreateDestination, intl, removeRois]
    );

    return { isProcessing, runUnion };
}

/**
 * DICOM ROI Generation Description of the result. Kept short and factual —
 * the structure names are not embedded because a long selection would blow
 * past the LO length limit and truncate mid-name.
 */
function buildGenerationDescription(request: BulkUnionRequest): string {
    const parts = [`Union of ${request.sourceRoiNumbers.length} structures`];
    if (request.fillHoles2d) {
        // Recorded in pipeline order — the fill runs before the margin.
        parts.push('2D holes filled');
    }
    if (request.marginMm !== 0) {
        parts.push(`margin ${request.marginMm.toFixed(1)} mm`);
    }
    if (request.minVolumeCm3 !== undefined) {
        parts.push(`min ${request.minVolumeCm3.toFixed(2)} cm3`);
    }
    // Recorded whenever the clip RUNS, contracted or not: at 0 mm it still
    // trims the union to the EXTERNAL surface, and it is on by default —
    // a geometry-changing stage missing from the provenance would make the
    // record disagree with the structure it describes.
    if (request.keepInsideRoiNumber !== null) {
        parts.push(
            request.keepInsideContractionMm > 0
                ? `kept ${request.keepInsideContractionMm.toFixed(1)} mm inside EXTERNAL`
                : 'kept inside EXTERNAL'
        );
    }
    return parts.join(', ');
}
